import React, { useState, useEffect } from 'react';
import Loading from '../../Common/Loading';
import popimg from "../../Common/Images/ImagesForAlert/popup_bg1.png";
import popbg from "../../Common/Images/ImagesForAlert/popup_bg2.png";
import popclose from "../../Common/Images/ImagesForAlert/popup_close_n.png";
import CustomAlert from '../../Common/CustomAlert';

// --- Define Props Interface ---
interface EL_AddModifyUpdatesProps {
    initialData: any | null; // null means 'Add' mode, object means 'Edit' mode
    onClose: (shouldRefresh?: boolean) => void;
}

const EL_AddModifyUpdates: React.FC<EL_AddModifyUpdatesProps> = ({ initialData, onClose }) => {
    const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

    // --- Form State ---
    const [updateId, setUpdateId] = useState<string | null>(null);
    const [updatedOn, setUpdatedOn] = useState<string>('');
    const [subject, setSubject] = useState<string>('');
    const [year, setYear] = useState<string>('');
    const [curriculum, setCurriculum] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    const [isActive, setIsActive] = useState<boolean>(false);
    const [country, setCountry] = useState<string>('');

    const [countryList, setCountryList] = useState<string[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [alertInfo, setAlertInfo] = useState({
        show: false,
        message: ""
    });

    document.body.style.backgroundColor = "azure";

    useEffect(() => {
        fetchCountries();
        initializeData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialData]);

    useEffect(() => {
        if (!country && countryList.length > 0) {
            setCountry(countryList[0]);
        }
    }, [country, countryList]);

    const fetchCountries = async () => {
        try {
            const res = await fetch(`${API_BASE_URL}api/updates/countries`);
            const json = await res.json();
            if (json.countries) {
                setCountryList(json.countries);
            }
        } catch (err) {
            console.error("Error fetching countries", err);
        }
    };

    const initializeData = () => {
        if (initialData) {
            // EDIT MODE
            setUpdateId(initialData.ID.toString());

            try {
                const dateObj = new Date(initialData.UpdatedOn);
                const offset = dateObj.getTimezoneOffset() * 60000;

                const localDate = new Date(
                    dateObj.getTime() - offset
                ).toISOString().slice(0, 10);

                setUpdatedOn(localDate);
            } catch {
                setUpdatedOn('');
            }

            setSubject(initialData.Subject || '');
            setYear(initialData.Year || '');
            setCurriculum(initialData.Curriculum || '');
            setCountry(initialData.Country || '');
            setIsActive(initialData.Status === 'A');
            setDescription(initialData.Description || '');

        } else {
            // ADD MODE
            const today = new Date().toISOString().split("T")[0];

            setUpdatedOn(today);

            setUpdateId(null);
            setSubject('');
            setYear('');
            setCurriculum('');
            setCountry('');
            setIsActive(false);
            setDescription('');
        }
    };
    const handleSave = async () => {
        setLoading(true);
        try {
            // Reformat date back to SQL friendly format if necessary
            const formattedDate = updatedOn || '';

            const payload = {
                id: updateId,
                updated_on: formattedDate,
                subject: subject,
                year: year,
                curriculum: curriculum,
                description: description,
                status: isActive ? 'A' : 'I',
                country: (country || '').trim()
            };

            const res = await fetch(`${API_BASE_URL}api/updates/save`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const json = await res.json();

            if (json.error) {
                setAlertInfo({
                    show: true,
                    message: json.error
                });
            } else {
                setAlertInfo({
                    show: true,
                    message: json.message
                });
            }
        } catch (err) {
            setAlertInfo({
                show: true,
                message: "An error occurred while saving."
            });
        } finally {
            setLoading(false);
        }
    };

    // --- Styles ---
    const styles = {
        overlay: {
            position: 'fixed' as const, top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 1000,
            display: 'flex', justifyContent: 'center', alignItems: 'center'
        },
        table: {
            fontFamily: 'Arial', fontSize: '11pt',
            borderBottom: '1px solid black', borderRight: '1px solid black',
            borderCollapse: 'collapse' as const, width: '100%',
            backgroundColor: "azure",
        },
        tdLabel: { borderLeft: '1px solid black', borderTop: '1px solid black', padding: '10px' },
        tdInput: { borderLeft: '1px solid black', borderTop: '1px solid black', padding: '10px' },
        inputBox: { border: '1px solid azure', outline: 'none', width: '100%', fontSize: '11pt', fontFamily: 'Arial', padding: '2px' },
        btn: { width: '100px', cursor: 'pointer', padding: '4px' }
    };

    return (
        <div style={styles.overlay}>
            <div style={{ width: "460px", background: "white", boxShadow: "0px 0px 12px rgba(0,0,0,0.5)" }}>

                {/* Popup Header */}
                <div
                    style={{
                        backgroundImage: `url(${popimg}), url(${popbg})`,
                        backgroundRepeat: "no-repeat, no-repeat",
                        backgroundPosition: "left center, right center",
                        backgroundSize: "30% 100%, 70% 100%",
                        height: "30px",
                        color: "white",
                        padding: "7px 10px",
                        fontWeight: "bold",
                        fontSize: "12pt",
                        fontFamily: "arial",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between"
                    }}
                >
                    <span>{updateId ? "Edit Updates" : "Add Updates"}</span>
                    <img
                        src={popclose}
                        alt="close"
                        height="25"
                        style={{ cursor: "pointer" }}
                        onClick={() => onClose(false)}
                    />
                </div>

                {/* Popup Body */}
                <div style={{ display: "flex", justifyContent: "center", padding: "20px", backgroundColor: "azure" }}>
                    {loading && <Loading />}

                    {alertInfo.show && (
                        <CustomAlert
                            message={alertInfo.message}
                            onClose={() => {
                                const isSuccess =
                                    !alertInfo.message.toLowerCase().includes("error");

                                setAlertInfo({
                                    show: false,
                                    message: ""
                                });

                                if (isSuccess) {
                                    onClose(true); // closes popup and refreshes parent
                                }
                            }}
                        />
                    )}

                    <table style={styles.table}>
                        <tbody>
                            <tr>
                                <td style={{ ...styles.tdLabel, width: '100px' }}>UpdatedOn:</td>
                                <td style={styles.tdInput}>
                                    <input
                                        type="date"
                                        style={styles.inputBox}
                                        value={updatedOn}
                                        onChange={(e) => setUpdatedOn(e.target.value)}
                                    />
                                </td>
                            </tr>
                            <tr>
                                <td style={styles.tdLabel}>Subject:</td>
                                <td style={styles.tdInput}>
                                    <input type="text" style={styles.inputBox} value={subject} onChange={(e) => setSubject(e.target.value)} />
                                </td>
                            </tr>
                            <tr>
                                <td style={styles.tdLabel}>Year:</td>
                                <td style={styles.tdInput}>
                                    <input type="text" style={styles.inputBox} value={year} onChange={(e) => setYear(e.target.value)} />
                                </td>
                            </tr>
                            <tr>
                                <td style={styles.tdLabel}>Curriculum:</td>
                                <td style={styles.tdInput}>
                                    <input type="text" style={styles.inputBox} value={curriculum} onChange={(e) => setCurriculum(e.target.value)} />
                                </td>
                            </tr>
                            <tr>
                                <td style={styles.tdLabel}>Description:</td>
                                <td style={styles.tdInput}>
                                    <input type="text" style={styles.inputBox} value={description} onChange={(e) => setDescription(e.target.value)} />
                                </td>
                            </tr>
                            <tr>
                                <td style={styles.tdLabel}>Status:</td>
                                <td style={styles.tdInput}>
                                    <label style={{ cursor: 'pointer' }}>
                                        <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} style={{ verticalAlign: 'middle', marginRight: '5px' }} />
                                        Active
                                    </label>
                                </td>
                            </tr>
                            <tr>
                                <td style={styles.tdLabel}>Country:</td>
                                <td style={styles.tdInput}>
                                    <select style={styles.inputBox} value={country} onChange={(e) => setCountry(e.target.value)}>
                                        {/* <option value="">-- Select --</option> */}
                                        {countryList.map((c) => (
                                            <option key={c} value={c}>{c}</option>
                                        ))}
                                    </select>
                                </td>
                            </tr>
                            <tr>
                                <td colSpan={2} style={{ borderLeft: '1px solid black', borderTop: '1px solid black', padding: '10px', textAlign: 'center' }}>
                                    <button style={styles.btn} onClick={handleSave}>Save</button>
                                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                                    <button style={styles.btn} onClick={() => onClose(false)}>Cancel</button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default EL_AddModifyUpdates;